import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { CheckCircle, Calculator, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Indicative pricing model:
 *  - Hourly trade rate ($60/h cost + 45% margin = $87/h customer-facing)
 *  - $110 callout fee that includes the first hour
 *  - Flat-rate jobs (TV/AV install, bond cleans) priced to roughly match
 *    JB Hi-Fi installation rates so customers see familiar numbers
 *
 * All figures are *estimates only* — final quote is always issued in writing.
 */
const HOURLY = 87;
const CALLOUT_INCLUDES_FIRST_HOUR = 110;

const services = {
  // Time-and-materials (hourly) jobs
  "General handyman repairs":        { type: "hourly" },
  "Plumbing repairs":                { type: "hourly", min: 1, premium: 1.15 },
  "Electrical repairs":              { type: "hourly", min: 1, premium: 1.15 },
  "Painting (interior & exterior)":  { type: "hourly", min: 2 },
  "Drywall & plastering":            { type: "hourly", min: 2 },
  "Carpentry":                       { type: "hourly", min: 1 },
  "Pressure washing":                { type: "hourly", min: 2 },
  "Gutter cleaning":                 { type: "hourly", min: 2 },
  "Window cleaning":                 { type: "hourly", min: 1 },
  "Lawn & garden maintenance":       { type: "hourly", min: 1 },
  "Regular property maintenance":    { type: "hourly", min: 1 },

  // Flat-rate jobs — bond cleans (priced per home size)
  "End-of-lease (bond) clean — 1-2 bed":   { type: "flat", price: 350 },
  "End-of-lease (bond) clean — 3 bed":     { type: "flat", price: 450 },
  "End-of-lease (bond) clean — 4+ bed":    { type: "flat", price: 550 },

  // Flat-rate jobs — TV / AV / Whitegoods installs (mirrors JB Hi-Fi installs)
  "TV wall mount install (up to 55\")":            { type: "flat", price: 129 },
  "TV wall mount install (over 55\")":             { type: "flat", price: 199 },
  "TV wall mount + in-wall cable concealment":     { type: "flat", price: 299 },
  "Soundbar wall mount + setup":                   { type: "flat", price: 129 },
  "Home theatre / surround sound setup":           { type: "flat", price: 199 },
  "Projector install (incl. ceiling mount)":       { type: "flat", price: 299 },
  "Smart home / Wi-Fi install":                    { type: "flat", price: 159 },
  "Antenna install (TV reception)":                { type: "flat", price: 199 },
  "Dishwasher install":                            { type: "flat", price: 169 },
  "Washing machine install":                       { type: "flat", price: 99 },
  "Dryer install (wall-mounted)":                  { type: "flat", price: 149 },
  "Fridge install / setup":                        { type: "flat", price: 99 },
};

const urgencyMultiplier = {
  "Flexible (this month)": 1.0,
  "Within a week": 1.05,
  "Urgent (24-48h)": 1.15,
};

function calcEstimate(service, hours, urgency) {
  const cfg = services[service];
  if (!cfg) return null;
  const u = urgencyMultiplier[urgency] || 1;

  if (cfg.type === "flat") {
    const base = Math.round(cfg.price * u);
    return { lo: base, hi: base, flat: true };
  }

  // Hourly: $110 callout (covers hr 1) + $87/h beyond
  const billableHours = Math.max(cfg.min || 1, hours);
  const extra = Math.max(0, billableHours - 1);
  const sub = CALLOUT_INCLUDES_FIRST_HOUR + extra * HOURLY;
  const subWithPremium = sub * (cfg.premium || 1) * u;
  // ±10% range to absorb materials & complexity
  const lo = Math.round((subWithPremium * 0.95) / 5) * 5;
  const hi = Math.round((subWithPremium * 1.10) / 5) * 5;
  return { lo, hi, flat: false };
}

export default function Quote() {
  usePageMeta({ title: "Free Quote", path: "/quote", description: "Get a free quote for any maintenance, repair or cleaning job in Albury/Wodonga. No deposits. No surprises." });
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [service, setService] = useState("");
  const [hours, setHours] = useState([2]);
  const [urgency, setUrgency] = useState("Within a week");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const isFlat = services[service]?.type === "flat";
  const estimate = useMemo(
    () => calcEstimate(service, hours[0], urgency),
    [service, hours, urgency],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!details.name || !details.email) return toast.error("Name and email required");
    setSubmitting(true);
    try {
      const estLine = estimate
        ? (estimate.flat ? `Flat-rate estimate $${estimate.lo}` : `Estimate $${estimate.lo}-$${estimate.hi}`)
        : "";
      const hoursLine = isFlat ? "Flat-rate job" : `Hours: ${hours[0]}`;
      await api.post("/leads", {
        ...details, service,
        source: "website-quote",
        message: `${estLine}. ${hoursLine}. Urgency: ${urgency}. Notes: ${details.message}`,
      });
      navigate("/thank-you", { state: { name: details.name, email: details.email, source: "quote" } });
    } catch { toast.error("Could not send"); setSubmitting(false); }
  };

  return (
    <div data-testid="quote-page">
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 min-h-[60vh] flex items-center">
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
          <div className="eyebrow">Free quote</div>
          <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">Get a price in 60 seconds.</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Indicative pricing only — your final quote is always in writing and free.</p>
        </div>
      </section>
      <section data-nav-theme="light" className="max-w-5xl mx-auto px-5 md:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 p-6 md:p-8 rounded-sm">
            <div className="flex gap-2 mb-6">
              {[1,2,3].map(i => (
                <div key={i} className={`flex-1 h-1 rounded-full ${step >= i ? "bg-navy" : "bg-slate-200"}`}/>
              ))}
            </div>
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">What service do you need?</Label>
                  <Select value={service} onValueChange={setService}>
                    <SelectTrigger data-testid="quote-service" className="rounded-sm mt-1 h-12"><SelectValue placeholder="Choose…"/></SelectTrigger>
                    <SelectContent>{Object.keys(services).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button data-testid="quote-next-1" onClick={()=>setStep(2)} disabled={!service} className="btn-gold rounded-sm h-12">Next <ArrowRight size={16} className="ml-2"/></Button>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-6">
                {isFlat ? (
                  <div className="bg-slate-50 border-l-2 border-navy p-4 text-sm text-slate-700">
                    This is a <strong>flat-rate</strong> job — the estimate on the right is the price you&apos;ll typically pay.
                    No need to estimate hours.
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Estimated job size</Label>
                    <div className="mt-3 flex items-center gap-4">
                      <Slider data-testid="quote-hours" value={hours} min={1} max={16} step={1} onValueChange={setHours} className="flex-1"/>
                      <div className="font-display text-3xl md:text-4xl tracking-wide text-navy w-20 text-right">{hours[0]}h</div>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">How urgent?</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger data-testid="quote-urgency" className="rounded-sm mt-1 h-12"><SelectValue/></SelectTrigger>
                    <SelectContent>{Object.keys(urgencyMultiplier).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={()=>setStep(1)} variant="outline" className="rounded-sm h-12">Back</Button>
                  <Button data-testid="quote-next-2" onClick={()=>setStep(3)} className="pill-navy flex-1 justify-center">Next <ArrowRight size={16} className="ml-2"/></Button>
                </div>
              </div>
            )}
            {step === 3 && (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Name</Label><Input data-testid="quote-name" required value={details.name} onChange={(e)=>setDetails({...details, name: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Phone</Label><Input data-testid="quote-phone" value={details.phone} onChange={(e)=>setDetails({...details, phone: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Email</Label><Input data-testid="quote-email" type="email" required value={details.email} onChange={(e)=>setDetails({...details, email: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Anything else?</Label><Textarea data-testid="quote-message" rows={3} value={details.message} onChange={(e)=>setDetails({...details, message: e.target.value})} placeholder="Address, photos, parking, access..." className="rounded-sm mt-1"/></div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={()=>setStep(2)} variant="outline" className="rounded-full h-12 px-6">Back</Button>
                  <Button data-testid="quote-submit" type="submit" disabled={submitting} className="pill-navy flex-1 justify-center !py-0 h-12">{submitting ? "Sending…" : "Send my quote request"}</Button>
                </div>
              </form>
            )}
          </div>
          <div>
            <div className="bg-navy text-white p-6 rounded-sm border-l-2 border-navy sticky top-24">
              <div className="flex items-center gap-2 mb-2"><Calculator size={18} className="text-white/70"/><div className="eyebrow !text-white/70 !text-[10px]">Indicative estimate</div></div>
              {estimate ? (
                <>
                  <div className="font-display text-4xl text-white mt-1">
                    {estimate.flat ? `$${estimate.lo}` : `$${estimate.lo}–$${estimate.hi}`}
                  </div>
                  <div className="text-xs text-slate-300 mt-2">
                    {estimate.flat
                      ? `Flat rate for ${service.toLowerCase()}, ${urgency.toLowerCase()}.`
                      : `Based on ${hours[0]}h of ${service.toLowerCase()}, ${urgency.toLowerCase()}.`}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-300 mt-2">Choose a service to see an estimate.</div>
              )}
              <ul className="mt-5 space-y-2 text-xs text-slate-300">
                <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-white mt-0.5"/>Final quote always in writing — free</li>
                <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-white mt-0.5"/>No deposits required</li>
                <li className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-white mt-0.5"/>Workmanship guarantee</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
